import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import {
  Drawer,
  ErrorState,
  Field,
  LoadingState,
  Select,
  cn,
} from "../../shared/ui";
import type { BaselineComparison, BaselineSummary } from "../../shared/types/api";
import {
  CATEGORY_LABELS,
  formatHours,
  formatPercent,
  formatSlippage,
  formatVariance,
} from "./projectVocabulary";

/**
 * Two baseline versions side by side (§100, §104).
 *
 * <p>Comparing is the reason for keeping versions at all. A pile of immutable snapshots nobody
 * can diff answers "what did we agree" but not "what changed, by how much, and why" — which is
 * the question a management review actually asks.
 */
export function BaselineCompareDrawer({
  projectId,
  versions,
  pair,
  onClose,
}: {
  projectId: string;
  /** Approved versions only: a draft is not yet a commitment to compare against. */
  versions: BaselineSummary[];
  pair: [BaselineSummary, BaselineSummary] | null;
  onClose: () => void;
}) {
  const [fromId, setFromId] = useState<string>("");
  const [toId, setToId] = useState<string>("");

  useEffect(() => {
    if (pair) {
      setFromId(pair[0].id);
      setToId(pair[1].id);
    }
  }, [pair]);

  const comparison = useQuery({
    queryKey: ["baseline-compare", projectId, fromId, toId],
    queryFn: () =>
      api.get<BaselineComparison>(
        `/api/v1/projects/${projectId}/baselines/compare?from=${fromId}&to=${toId}`,
      ),
    enabled: Boolean(pair) && Boolean(fromId) && Boolean(toId) && fromId !== toId,
  });

  return (
    <Drawer
      open={Boolean(pair)}
      onClose={onClose}
      title="Compare baselines"
      subtitle="What changed between two agreed commitments."
      width="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="From version">
            <Select value={fromId} onChange={(event) => setFromId(event.target.value)}>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  V{version.versionNumber} · {formatHours(version.approvedHours)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="To version">
            <Select value={toId} onChange={(event) => setToId(event.target.value)}>
              {versions.map((version) => (
                <option key={version.id} value={version.id}>
                  V{version.versionNumber} · {formatHours(version.approvedHours)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {fromId === toId && (
          <p className="text-sm text-ink-500">Choose two different versions to compare.</p>
        )}

        {comparison.isPending && fromId !== toId && <LoadingState label="Comparing…" />}
        {comparison.isError && <ErrorState error={comparison.error} />}

        {comparison.isSuccess && (
          <>
            <div className="rounded-lg border border-ink-200 bg-ink-50 px-4 py-3">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-ink-500">V{comparison.data.fromVersion}</p>
                  <p className="tnum mt-0.5 font-semibold text-ink-900">
                    {formatHours(comparison.data.fromHours)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-ink-500">Change</p>
                  <p
                    className={cn(
                      "tnum font-display mt-0.5 text-lg font-semibold",
                      comparison.data.hoursDelta > 0
                        ? "text-red-600"
                        : comparison.data.hoursDelta < 0
                          ? "text-emerald-600"
                          : "text-ink-900",
                    )}
                  >
                    {formatVariance(comparison.data.hoursDelta)}
                  </p>
                  <p className="text-xs text-ink-400">
                    {formatPercent(comparison.data.hoursDeltaPercent)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-ink-500">V{comparison.data.toVersion}</p>
                  <p className="tnum mt-0.5 font-semibold text-ink-900">
                    {formatHours(comparison.data.toHours)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-ink-200 px-4 py-2.5 text-sm">
              <span className="text-ink-500">Committed finish</span>
              <span className="tnum text-ink-900">
                {comparison.data.fromFinish} → {comparison.data.toFinish}
                <span
                  className={cn(
                    "ml-2",
                    comparison.data.finishDeltaDays > 0 ? "text-red-600" : "text-emerald-600",
                  )}
                >
                  ({formatSlippage(comparison.data.finishDeltaDays)})
                </span>
              </span>
            </div>

            {comparison.data.reason && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                <p className="text-xs font-medium text-amber-700">
                  Why V{comparison.data.toVersion} was agreed
                </p>
                <p className="mt-0.5 text-sm text-amber-700/90">{comparison.data.reason}</p>
              </div>
            )}

            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink-900">By category</h3>
              <div className="overflow-hidden rounded-lg border border-ink-200">
                <div className="flex items-center gap-3 border-b border-ink-100 bg-ink-50/60 px-3 py-2 text-xs font-medium text-ink-500">
                  <span className="flex-1">Category</span>
                  <span className="tnum w-20 text-right">V{comparison.data.fromVersion}</span>
                  <span className="tnum w-20 text-right">V{comparison.data.toVersion}</span>
                  <span className="tnum w-20 text-right">Change</span>
                </div>
                {comparison.data.categoryDeltas.map((delta) => (
                  <div
                    key={delta.category}
                    className={cn(
                      "flex items-center gap-3 border-b border-ink-100 px-3 py-2 text-sm last:border-0",
                      delta.deltaHours !== 0 && "bg-amber-50/40",
                    )}
                  >
                    <span className="flex-1 text-ink-700">
                      {CATEGORY_LABELS[delta.category]}
                    </span>
                    <span className="tnum w-20 text-right text-ink-500">
                      {formatHours(delta.fromHours)}
                    </span>
                    <span className="tnum w-20 text-right text-ink-900">
                      {formatHours(delta.toHours)}
                    </span>
                    <span
                      className={cn(
                        "tnum w-20 text-right font-medium",
                        delta.deltaHours > 0
                          ? "text-red-600"
                          : delta.deltaHours < 0
                            ? "text-emerald-600"
                            : "text-ink-400",
                      )}
                    >
                      {delta.deltaHours === 0 ? "—" : formatVariance(delta.deltaHours)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </Drawer>
  );
}
